import { useEffect, useState } from 'react'
import { get, isEmpty } from 'lodash'
import { useMutation, useQuery } from 'react-query'
import { useSnackbar } from 'notistack'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material'

import { participantRoutes, SurveyParticipantsType } from './participantRoutes'
import { UserAvatar } from 'components'
import { API } from 'core/requests'
import { t } from 'i18n'
import { getUserFullName } from 'utils/user'

type Props = {
  open: boolean
  surveyId: number | string | null | undefined
  surveyType: SurveyParticipantsType
  surveyName?: string
  onClose: () => void
  onSuccess?: () => void
}

export default function SurveyInviteParticipantsDialog({
  open,
  surveyId,
  surveyType,
  surveyName,
  onClose,
  onSuccess,
}: Props) {
  const { enqueueSnackbar } = useSnackbar()
  const routes = participantRoutes[surveyType]

  const [selectedEmployees, setSelectedEmployees] = useState<any[]>([])

  const { data: uninvitedUsers, isFetching: uninvitedUsersIsLoading } =
    useQuery(
      [routes.uninvited, surveyId],
      () =>
        API.base(routes.uninvited, {
          method: 'GET',
          pathParams: { id: surveyId },
        }),
      { enabled: open && !!surveyId },
    )

  useEffect(() => {
    if (!open) {
      setSelectedEmployees([])
    }
  }, [open])

  const inviteParticipantsMutation = useMutation(
    (userIds: number[]) =>
      API.base(routes.add, {
        method: 'POST',
        pathParams: { id: surveyId },
        body: { user_ids: userIds },
      }),
    {
      onSuccess: (response: any) => {
        if (response?.success) {
          enqueueSnackbar(
            response.message ??
              t('pulseSurveyOverview.employeesInvitation.inviteEmployees'),
            { variant: 'success' },
          )
          setSelectedEmployees([])
          onSuccess?.()
          onClose()
        } else {
          enqueueSnackbar(
            response?.message ??
              t('pulseSurveyOverview.employeesInvitation.errorMessage'),
            { variant: 'error' },
          )
        }
      },
      onError: () => {
        enqueueSnackbar(
          t('pulseSurveyOverview.employeesInvitation.errorMessage'),
          { variant: 'error' },
        )
      },
    },
  )

  const handleClose = () => {
    onClose()
  }

  const handleInvite = () => {
    inviteParticipantsMutation.mutate(
      selectedEmployees.map(employee => employee.id),
    )
  }

  return (
    <Dialog
      open={open}
      maxWidth='sm'
      fullWidth
      onClose={handleClose}
      aria-labelledby='invite-participants-dialog-title'
      aria-describedby='invite-participants-dialog-description'
    >
      <DialogTitle id='invite-participants-dialog-title'>
        {t('pulseSurveyOverview.employeesInvitation.inviteEmployees')}
      </DialogTitle>
      <DialogContent>
        <Grid item xs={12}>
          <Typography variant='body2' color='text.secondary' paragraph>
            {t('pulseSurveyOverview.employeesInvitation.chooseEmployees')}{' '}
            {surveyName}.
          </Typography>
        </Grid>
        <Autocomplete
          multiple
          id='pulseSurveyUninvitedUsers'
          data-cy='pulse_invite_participants_autocomplete'
          value={selectedEmployees}
          onChange={(event: any, newValue: any | null) => {
            setSelectedEmployees(newValue || [])
          }}
          autoHighlight
          loading={uninvitedUsersIsLoading}
          options={uninvitedUsers || []}
          getOptionLabel={employee => getUserFullName(employee)}
          renderOption={(props, employee: any) => (
            <Box
              component='li'
              sx={{ '& > img': { mr: 3, flexShrink: 0 } }}
              {...props}
            >
              <UserAvatar
                userId={get(employee, 'id')}
                name={get(employee, 'first_name')}
                src={get(employee, 'avatar.url')}
                size={24}
              />
              <Box component='span' ml={1}>
                {getUserFullName(employee)}
              </Box>
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label={t('pulseSurveyOverview.employeesInvitation.textFieldLabel')}
              variant='outlined'
              inputProps={{
                ...params.inputProps,
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color='primary'
          data-cy='pulse_invite_participants_cancel'
        >
          {t('pulseSurveyOverview.employeesInvitation.cancelButton')}
        </Button>
        <Button
          onClick={handleInvite}
          color='primary'
          disabled={
            inviteParticipantsMutation.isLoading || isEmpty(selectedEmployees)
          }
          data-cy='pulse_invite_participants_submit'
          autoFocus
        >
          {inviteParticipantsMutation.isLoading ? (
            <CircularProgress size={24} />
          ) : (
            t('pulseSurveyOverview.employeesInvitation.inviteButton')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
